// ============================================================================
// The Hive — module registry
// Single source of truth for navigation + hash routing. Each module renders into
// the content area. Phase 1 ships the shell: every module is a rich placeholder
// describing its scope; behavior is filled in module-by-module in later phases.
// ============================================================================

import { stub } from '../lib/view.js'
import { icon } from '../lib/icons.js'
import { renderTrap } from './trap.js'
import { renderPacker } from './packer.js'
import { renderReports } from './reports.js'
import { renderLocker } from './locker.js'
import { renderTouch } from './touch.js'
import { renderCampaigns } from './campaigns.js'
import { renderAutoreply } from './autoreply.js'
import { renderSettings } from './settings.js'
import { renderCopilot } from './copilot.js'
import { renderOverview } from './overview.js'
import { renderWebhooks } from './webhooks.js'

/**
 * @typedef {Object} HiveModule
 * @property {string} id     hash route id, e.g. "packer" -> #/packer
 * @property {string} label  sidebar label
 * @property {string} group  sidebar section
 * @property {string} icon   icon key (lib/icons.js)
 * @property {string} role   one-line role
 * @property {string} title  topbar title
 * @property {string} sub    topbar subtitle
 * @property {string[]} plan  Phase-1 scope bullets (what this module will do)
 * @property {('live'|'soon'|'deferred')} status
 * @property {(el:HTMLElement)=>void} [render]  custom render; defaults to stub()
 */

/** @type {HiveModule[]} */
export const MODULES = [
  {
    id: 'overview', label: 'Overview', group: 'The Hive', icon: 'hive',
    role: 'Command center', title: 'The Hive', status: 'live',
    sub: 'Messaging, deliverability & compliance — at a glance',
    render: (el) => renderOverview(el),
    plan: [],
  },

  // ---- Messaging ----
  {
    id: 'packer', label: 'HoneyPacker', group: 'Messaging', icon: 'send',
    role: 'Bulk SMS sender', title: 'HoneyPacker', status: 'live',
    sub: 'Compliant bulk send with consent gating + suppression',
    render: (el) => renderPacker(el),
    plan: [],
  },
  {
    id: 'touch', label: 'HoneyTouch', group: 'Messaging', icon: 'inbox',
    role: 'Comm center + unified inbox', title: 'HoneyTouch', status: 'live',
    sub: 'One inbox across your SignalHouse numbers',
    render: (el) => renderTouch(el),
    plan: [],
  },
  {
    id: 'campaigns', label: 'Campaigns', group: 'Messaging', icon: 'megaphone',
    role: 'Named campaigns + reporting', title: 'Campaigns', status: 'live',
    sub: 'Per-campaign delivery, opt-out & click performance',
    render: (el) => renderCampaigns(el),
    plan: [],
  },
  {
    id: 'autoreply', label: 'Auto-reply', group: 'Messaging', icon: 'bolt',
    role: 'Inbound automation', title: 'Auto-reply', status: 'live',
    sub: 'Keyword + AI-classified inbound responses',
    render: (el) => renderAutoreply(el),
    plan: [],
  },

  // ---- Compliance & Data ----
  {
    id: 'trap', label: 'HoneyTrap', group: 'Compliance & Data', icon: 'shield',
    role: 'DNC / suppression engine', title: 'HoneyTrap', status: 'live',
    sub: 'Unified opt-out capture + enforcement, all providers',
    render: (el) => renderTrap(el),
    plan: [],
  },
  {
    id: 'locker', label: 'HoneyLocker', group: 'Compliance & Data', icon: 'search',
    role: 'Customer 360 + message search', title: 'HoneyLocker', status: 'live',
    sub: 'Cross-provider lookup — every PII access is logged',
    render: (el) => renderLocker(el),
    plan: [],
  },
  {
    id: 'compliance', label: 'Compliance & Deliverability', group: 'Compliance & Data', icon: 'gauge',
    role: 'Reporting + governance', title: 'Compliance & Deliverability', status: 'live',
    sub: 'SMS + Email deliverability, A2P health, audit exports',
    render: (el) => renderReports(el),
    plan: [],
  },

  {
    id: 'copilot', label: 'Hive Copilot', group: 'Compliance & Data', icon: 'spark',
    role: 'Built-in AI assistant', title: 'Hive Copilot', status: 'live',
    sub: 'Ask your data, draft compliant messages, diagnose deliverability',
    render: (el) => renderCopilot(el),
    plan: [],
  },

  // ---- Admin ----
  {
    id: 'webhooks', label: 'Webhooks', group: 'Admin', icon: 'webhook',
    role: 'Inbound webhook monitor', title: 'Webhooks', status: 'live',
    sub: 'Track the endpoints providers call + live event feed',
    render: (el) => renderWebhooks(el),
    plan: [],
  },
  {
    id: 'settings', label: 'Settings & Access', group: 'Admin', icon: 'cog',
    role: 'Users, roles, secrets, retention', title: 'Settings & Access', status: 'live',
    sub: 'RBAC, audit log, data retention',
    render: (el) => renderSettings(el),
    plan: [],
  },

  // ---- Deferred ----
  {
    id: 'desk', label: 'Trap Phone', group: 'Deferred', icon: 'headset',
    role: 'Customer-service console', title: 'Trap Phone', status: 'deferred',
    sub: 'Deferred — the agent workflow layer comes later',
    plan: [
      'Agent inbox, assign/resolve, topic triage, canned answers + AI suggestions, customer journey',
      'Deferred per scope: the Twilio thread/send engine lifts into HoneyTouch; only this workflow layer waits',
    ],
  },
]

export function moduleById(id) {
  return MODULES.find(m => m.id === id) || MODULES[0]
}

// Attach the default renderer + resolve icons.
for (const m of MODULES) {
  if (!m.render) m.render = (el) => stub(el, m)
  m.iconSvg = icon(m.icon)
}
